import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { format, parse, isValid, getYear } from 'date-fns';
import { IconButton, InputAdornment, Typography, TextField } from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useMuiError } from 'hooks';
import { defaultMuiProps } from '../config';
import { useLocale } from 'hooks';
import { PickersLayout } from '@mui/x-date-pickers/PickersLayout';
import { startOfDay, startOfMonth } from 'date-fns';
import InputMask from 'react-input-mask';

const FlexPickersLayout = styled(PickersLayout)(({ theme }) => ({
  display: 'flex !important',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'flex-start',
  width: '100%'
}));

const ToolbarContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(2),
  cursor: 'pointer',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: theme.color.text.highlight
}));

const YearLabel = styled(Typography)(({ theme }) => ({
  color: '#FFFFFFB3',
  cursor: 'pointer'
}));

const DayLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  cursor: 'pointer'
}));

function CustomToolbar({ value, setView, view, locale }) {
  const displayDate = value || new Date();

  const isYearView = view === 'year';
  const now = new Date();

  const yearColor = isYearView ? '#FFFFFF' : 'rgba(255, 255, 255, 0.54)';

  const dayColor = !isYearView ? '#FFFFFF' : 'rgba(255, 255, 255, 0.54)';

  return (
    <ToolbarContainer>
      <YearLabel
        variant="h6"
        onClick={() => setView('year')}
        style={{ color: yearColor, fontSize: '1rem' }}
      >
        {format(displayDate, 'yyyy', { locale })}
      </YearLabel>
      <DayLabel variant="h4" onClick={() => setView('day')} style={{ color: dayColor }}>
        {format(displayDate, 'EEE, MMM d', { locale })}
      </DayLabel>
    </ToolbarContainer>
  );
}

function CustomCalendarHeader({
  currentMonth,
  onMonthChange,
  locale,
  view,
  disableFutureDates,
  disablePast,
  minDate
}) {
  if (view === 'year' || view === 'month') return null;

  const today = startOfMonth(new Date());
  const currentMonthStart = startOfMonth(currentMonth);

  let minMonth = null;
  if (minDate) minMonth = startOfMonth(parse(minDate, 'yyyy-MM-dd', new Date()));
  else if (disablePast) minMonth = today;

  const maxMonth = disableFutureDates ? today : null;

  const isPrevDisabled = minMonth ? currentMonthStart <= minMonth : false;
  const isNextDisabled = maxMonth ? currentMonthStart >= maxMonth : false;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 8px',
        margin: '8px 0',
        position: 'relative'
      }}
    >
      <IconButton
        onClick={() => onMonthChange(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
        disabled={isPrevDisabled}
      >
        <ChevronLeftIcon />
      </IconButton>

      <Typography
        variant="subtitle1"
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          fontWeight: 500
        }}
      >
        {format(currentMonth, 'MMMM yyyy', { locale })}
      </Typography>

      <IconButton
        onClick={() => onMonthChange(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
        disabled={isNextDisabled}
      >
        <ChevronRightIcon />
      </IconButton>
    </div>
  );
}

const singleColumnYearTheme = createTheme({
  components: {
    MuiPickersYear: {
      styleOverrides: {
        root: {
          flexBasis: '100% !important',
          maxWidth: '100% !important',
          justifyContent: 'center',
          height: 40,
          margin: '4px 0'
        },
        yearButton: {
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          background: 'none !important',
          '&:hover': { background: 'none !important' },
          '&:active': { background: 'none !important' },
          '&.Mui-selected': {
            fontWeight: 500,
            color: '#206AF5',
            fontSize: '1.5rem'
          }
        }
      }
    },
    MuiPickersMonth: {
      styleOverrides: {
        root: {
          flex: '1 0 33%',
          maxWidth: '33%',
          justifyContent: 'center',
          margin: '4px 0'
        },
        monthButton: {
          width: '100%',
          height: 52,
          justifyContent: 'center',
          background: 'none !important',
          '&:hover': { background: 'none !important' },
          '&:active': { background: 'none !important' },
          '&.Mui-selected': {
            fontWeight: 500,
            color: '#206AF5',
            fontSize: '1.5rem'
          }
        }
      }
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          height: '310px',
          maxHeight: '310px',
          minHeight: '310px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.MuiPickersDay-today': {
            border: '1px solid transparent',
            color: '#206AF5'
          },
          '&.Mui-selected': {
            backgroundColor: '#206AF5',
            color: '#fff'
          }
        }
      }
    }
  }
});

const MaskedInput = React.forwardRef(function MaskedInput(props, ref) {
  return (
    <InputMask {...props} mask="99-99-9999" maskChar="_">
      {inputProps => <input ref={ref} {...inputProps} />}
    </InputMask>
  );
});

function DateField({
  error,
  minDate,
  value,
  wizardMode = false,
  onChange,
  InputLabelProps = { shrink: true },
  disabled = false,
  disableFutureYears = false,
  disableFutureDates = false,
  disablePast = false,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const locale = useLocale();
  const [view, setView] = useState(wizardMode ? 'year' : 'day');
  const [inputValue, setInputValue] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [pickerValue, setPickerValue] = useState(null);
  const dynamicViews = view === 'year' ? ['year'] : wizardMode ? ['year', 'month', 'day'] : ['day'];
  const initialView = view;
  const inputRef = useRef(null);
  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return isValid(value) ? value : null;
    const parsed = parse(value, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? parsed : null;
  }, [value]);

  const formattedValue = useMemo(() => {
    if (!parsedValue) return '';
    return format(parsedValue, 'dd-MM-yyyy');
  }, [parsedValue]);

  useEffect(() => {
    if (value && formattedValue !== inputValue && !isInputFocused) {
      setInputValue(formattedValue);
    }
  }, [value, formattedValue, inputValue, isInputFocused]);

  useEffect(() => {
    if (open) {
      setPickerValue(parsedValue);
    }
  }, [open, parsedValue]);

  useEffect(() => {
    if (disabled) {
      if (parsedValue) {
        onChange('');
        setInputValue('');
      }
    }
  }, [disabled]);

  const effectiveMinDate = useMemo(() => {
    if (minDate) {
      return parse(minDate, 'yyyy-MM-dd', new Date());
    }
    if (disablePast) {
      return startOfDay(new Date());
    }
    return null;
  }, [minDate, disablePast]);

  const handleChange = useCallback(
    date => {
      if (!disabled) {
        if (date instanceof Date && isValid(date)) {
          const formattedDate = format(date, 'yyyy-MM-dd');
          onChange(formattedDate);
        } else {
          onChange('');
        }
      }
    },
    [onChange, disabled]
  );

  const handleInputChange = useCallback(
    event => {
      const rawValue = event.target.value;
      setInputValue(rawValue);

      if (rawValue.replace(/_/g, '').length === 8) {
        try {
          const parsed = parse(rawValue, 'dd-MM-yyyy', new Date());
          if (isValid(parsed)) {
            handleChange(parsed);
          }
        } catch (error) {}
      } else if (rawValue === '') {
        onChange('');
      }
    },
    [handleChange, onChange]
  );

  const handleInputFocus = useCallback(e => {
    setIsInputFocused(true);
  }, []);

  const handleInputBlur = useCallback(
    e => {
      setIsInputFocused(false);

      const rawValue = e.target.value;
      if (rawValue && rawValue.replace(/_/g, '').length > 0) {
        try {
          const parsed = parse(rawValue, 'dd-MM-yyyy', new Date());
          if (isValid(parsed)) {
            handleChange(parsed);
          } else {
            setInputValue(formattedValue);
          }
        } catch (error) {
          setInputValue(formattedValue);
        }
      } else {
        onChange('');
      }
    },
    [handleChange, formattedValue, onChange]
  );

  const computedError = useMemo(() => {
    if (!inputValue) return '';

    const cleaned = inputValue.replace(/_/g, '');
    if (cleaned.length < 8) return '';

    const parsed = parse(inputValue, 'dd-MM-yyyy', new Date());
    if (!isValid(parsed)) {
      return 'Invalid date format';
    }

    const today = startOfDay(new Date());
    const compareDate = startOfDay(parsed);

    if (disablePast && compareDate < today) {
      return 'Date should not be before minimal date';
    }

    if (disableFutureDates && compareDate > today) {
      return 'Date should not be after maximal date';
    }

    if (effectiveMinDate && compareDate < effectiveMinDate) {
      return 'Date should not be before minimal date';
    }

    return '';
  }, [inputValue, effectiveMinDate, disablePast, disableFutureDates]);

  const disablePastYears = yearDate => getYear(yearDate) < getYear(new Date());
  const now = new Date();
  const today = startOfDay(new Date());

  return (
    <ThemeProvider theme={singleColumnYearTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        {/*  DATE PICKER  */}
        <MobileDatePicker
          open={open}
          onOpen={() => !disabled && setOpen(true)}
          onClose={() => setOpen(false)}
          openOnFocus={false}
          value={pickerValue ?? parsedValue ?? null}
          onChange={date => setPickerValue(date)}
          onAccept={date => {
            handleChange(date);
            setOpen(false);
          }}
          views={dynamicViews}
          openTo={initialView}
          onViewChange={setView}
          minDate={effectiveMinDate || undefined}
          onYearChange={() => wizardMode && setView('month')}
          onMonthChange={() => wizardMode && setView('day')}
          dayOfWeekFormatter={(day, date) => {
            return format(date, 'EEEEEE', { locale });
          }}
          shouldDisableYear={yearDate => {
            const year = getYear(yearDate);
            const currentYear = getYear(new Date());

            if (disableFutureYears && year > currentYear) return true;
            if (effectiveMinDate) {
              const minYear = getYear(effectiveMinDate);
              if (year < minYear) return true;
            }
            if (disablePast && year < currentYear) return true;
            return false;
          }}
          shouldDisableMonth={monthDate => {
            const today = new Date();
            const currentMonth = startOfMonth(today);
            const compareMonth = startOfMonth(monthDate);

            if (disableFutureDates && compareMonth > currentMonth) return true;
            if (effectiveMinDate) {
              const min = startOfMonth(effectiveMinDate);
              if (compareMonth < min) return true;
            }
            if (disablePast && compareMonth < currentMonth) return true;
            return false;
          }}
          shouldDisableDate={date => {
            const today = startOfDay(new Date());
            const compareDate = startOfDay(date);

            if (disableFutureDates && compareDate > today) return true;
            if (effectiveMinDate) {
              const min = startOfDay(effectiveMinDate);
              if (compareDate < min) return true;
            }
            if (disablePast && compareDate < today) return true;
            return false;
          }}
          disabled={disabled}
          format="dd-MM-yyyy"
          slots={{
            layout: FlexPickersLayout,
            toolbar: props => <CustomToolbar {...props} setView={setView} locale={locale} />,
            calendarHeader: props => (
              <CustomCalendarHeader
                {...props}
                locale={locale}
                view={view}
                disableFutureYears={disableFutureYears}
                disableFutureDates={disableFutureDates}
                disablePast={disablePast}
                minDate={minDate}
              />
            ),
            textField: () => null
          }}
          reduceAnimations
        />
        <TextField
          {...defaultMuiProps}
          {...useMuiError(error)}
          helperText={computedError || error}
          InputLabelProps={InputLabelProps}
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          inputRef={inputRef}
          InputProps={{
            inputComponent: disabled ? undefined : MaskedInput,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => !disabled && setOpen(true)}
                  disabled={disabled}
                  sx={{
                    color: disabled ? theme => theme.palette.action.disabled : 'inherit',
                    cursor: disabled ? 'default' : 'pointer'
                  }}
                >
                  <CalendarTodayIcon />
                </IconButton>
              </InputAdornment>
            ),
            readOnly: false
          }}
          {...rest}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
}

DateField.propTypes = {
  error: PropTypes.string,
  minDate: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  wizardMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  InputLabelProps: PropTypes.object
};

export default React.memo(DateField);
